<template>
  <div class="container py-4">
    <div class="d-flex align-items-center mb-4">
      <i class="bi bi-check2-square fs-4 me-2 text-primary"></i>
      <h2 class="mb-0">Tareas Pendientes</h2>
      <span v-if="!cargando && tarjetas.length > 0" class="badge bg-secondary ms-2">{{ tarjetas.length }}</span>
      <button class="btn btn-outline-secondary btn-sm ms-auto" @click="cargarTareas" :disabled="cargando">
        <i class="bi bi-arrow-clockwise me-1"></i>Actualizar
      </button>
    </div>

    <!-- Spinner de carga -->
    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Cargando...</span>
      </div>
      <p class="text-muted mt-2">Cargando tareas desde Trello...</p>
    </div>

    <!-- Error -->
    <div v-else-if="error" class="alert alert-danger">
      <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
    </div>

    <!-- Contenido -->
    <div v-else>
      <!-- Tabs -->
      <ul class="nav nav-tabs mb-3">
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: tabActivo === 'IN PROGRESS' }"
            @click="tabActivo = 'IN PROGRESS'"
          >
            <i class="bi bi-play-circle me-1"></i>En Progreso
            <span class="badge rounded-pill ms-1" :class="inProgress.length > 0 ? 'bg-primary' : 'bg-secondary'">
              {{ inProgress.length }}
            </span>
          </button>
        </li>
        <li class="nav-item">
          <button
            class="nav-link"
            :class="{ active: tabActivo === 'INBOX' }"
            @click="tabActivo = 'INBOX'"
          >
            <i class="bi bi-inbox me-1"></i>Inbox
            <span class="badge rounded-pill ms-1" :class="inbox.length > 0 ? 'bg-primary' : 'bg-secondary'">
              {{ inbox.length }}
            </span>
          </button>
        </li>
      </ul>

      <!-- Lista de tarjetas -->
      <div v-if="tarjetasActivas.length === 0" class="text-center py-5 text-muted">
        <i class="bi bi-check-circle fs-1 d-block mb-2"></i>
        No hay tareas pendientes en esta lista.
      </div>

      <div v-else class="accordion" id="tareasAccordion">
        <div
          v-for="(tarjeta, idx) in tarjetasActivas"
          :key="tarjeta.id"
          class="accordion-item mb-2 border rounded"
        >
          <h2 class="accordion-header">
            <button
              class="accordion-button collapsed py-2 px-3"
              type="button"
              @click="toggleTarjeta(tarjeta.id)"
              :aria-expanded="expandida === tarjeta.id"
            >
              <div class="d-flex align-items-start flex-grow-1 me-2 gap-2 flex-wrap">
                <!-- Badges de etiquetas -->
                <span
                  v-for="label in tarjeta.labels"
                  :key="label.id"
                  class="badge"
                  :style="{ backgroundColor: colorLabel(label.color) }"
                  style="font-size: 0.7rem;"
                >
                  {{ label.name || label.color }}
                </span>

                <!-- Nombre -->
                <span class="fw-semibold text-dark">{{ tarjeta.name }}</span>

                <!-- Fecha límite -->
                <span
                  v-if="tarjeta.due"
                  class="badge ms-auto"
                  :class="esFechaVencida(tarjeta.due) ? 'bg-danger' : 'bg-success'"
                  style="font-size: 0.7rem; white-space: nowrap;"
                >
                  <i class="bi bi-calendar me-1"></i>{{ formatFecha(tarjeta.due) }}
                </span>
              </div>
            </button>
          </h2>

          <!-- Cuerpo expandible -->
          <div v-show="expandida === tarjeta.id" class="accordion-collapse">
            <div class="accordion-body py-3 px-3">
              <!-- Descripción -->
              <div v-if="tarjeta.desc" class="mb-3">
                <p class="text-muted small fw-semibold mb-1">
                  <i class="bi bi-card-text me-1"></i>Descripción
                </p>
                <div
                  class="bg-light rounded p-3 desc-content"
                  v-html="renderDesc(tarjeta.desc)"
                ></div>
              </div>
              <div v-else class="text-muted fst-italic small mb-3">
                Sin descripción
              </div>

              <!-- Botón Trello -->
              <a
                :href="tarjeta.url"
                target="_blank"
                rel="noopener noreferrer"
                class="btn btn-outline-primary btn-sm"
              >
                <i class="bi bi-trello me-1"></i>Ver en Trello
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from '../config/axios';
import { setTareasCount } from '../utils/navBadges';

export default {
  name: 'Tareas',
  setup() {
    const tarjetas = ref([]);
    const cargando = ref(false);
    const error = ref('');
    const tabActivo = ref('IN PROGRESS');
    const expandida = ref(null);

    const inProgress = computed(() => tarjetas.value.filter(t => t.lista === 'IN PROGRESS'));
    const inbox = computed(() => tarjetas.value.filter(t => t.lista === 'INBOX'));
    const tarjetasActivas = computed(() => tabActivo.value === 'IN PROGRESS' ? inProgress.value : inbox.value);

    async function cargarTareas() {
      cargando.value = true;
      error.value = '';
      expandida.value = null;
      try {
        const { data } = await axios.get('/api/tareas');
        tarjetas.value = data.tarjetas;
        setTareasCount(data.total);
      } catch (err) {
        error.value = err.response?.data?.error || 'Error al cargar tareas';
      } finally {
        cargando.value = false;
      }
    }

    function toggleTarjeta(id) {
      expandida.value = expandida.value === id ? null : id;
    }

    function formatFecha(isoDate) {
      if (!isoDate) return '';
      const d = new Date(isoDate);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
    }

    function esFechaVencida(isoDate) {
      return new Date(isoDate) < new Date();
    }

    function colorLabel(color) {
      const mapa = {
        green: '#61bd4f',
        yellow: '#f2d600',
        orange: '#ff9f1a',
        red: '#eb5a46',
        purple: '#c377e0',
        blue: '#0079bf',
        sky: '#00c2e0',
        lime: '#51e898',
        pink: '#ff78cb',
        black: '#344563'
      };
      return mapa[color] || '#aaa';
    }

    function renderDesc(desc) {
      if (!desc) return '';
      // Detectar links markdown [texto](url) y URLs sueltas
      let html = desc
        // Markdown links
        .replace(/\[([^\]]+)\]\(([^)\s"]+)[^)]*\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>')
        // URLs sueltas no precedidas de href="
        .replace(/(?<!href=["'])https?:\/\/[^\s<]+/g, url => `<a href="${url}" target="_blank" rel="noopener">${url}</a>`)
        // Negrita markdown **texto**
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        // Saltos de línea
        .replace(/\n/g, '<br>');
      return html;
    }

    onMounted(cargarTareas);

    return {
      tarjetas,
      cargando,
      error,
      tabActivo,
      expandida,
      inProgress,
      inbox,
      tarjetasActivas,
      cargarTareas,
      toggleTarjeta,
      formatFecha,
      esFechaVencida,
      colorLabel,
      renderDesc
    };
  }
};
</script>

<style scoped>
.accordion-button:not(.collapsed) {
  background-color: #f8f9fa;
  color: inherit;
  box-shadow: none;
}
.accordion-button::after {
  display: none;
}
.accordion-button {
  cursor: pointer;
}
.desc-content {
  font-size: 0.9rem;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
